import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, throwError } from 'rxjs'
import { catchError, distinctUntilChanged, map, startWith, tap } from 'rxjs/operators'
import * as lodash from 'lodash'

import * as fromRoot from '@tv3/store/state'
import {
  Channel,
  ChannelAdjustment,
  ChannelAdjustmentService,
  Destroyable,
  LoadChannelAdjustmentsComplete,
  Rental,
  SaveForm,
  selectAllApiChannelViews,
  selectAllRentals,
  Toaster,
  tokeetDashboardChannel,
  untilDestroy,
} from '@tokeet/tv3-platform'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import * as R from 'ramda'
import { AmplitudeService } from '@tv3/services/amplitude.service'

export interface EditChannelAdjustmentDialogInput {
  channelAdjustment?: ChannelAdjustment
}

export interface EditChannelAdjustmentDialogResult {
  channelAdjustment: ChannelAdjustment
  channelAdjustments: ChannelAdjustment[]
}

export type AdjustType = 'amount' | 'percent'

@Component({
  selector: 'app-edit-channel-adjustment-dialog',
  // tslint:disable use-host-property-decorator
  host: { class: 'modal-content' },
  templateUrl: './edit-channel-adjustment-dialog.component.html',
  styleUrls: ['./edit-channel-adjustment-dialog.component.scss'],
})
export class EditChannelAdjustmentDialogComponent extends Destroyable implements OnInit, OnDestroy {
  isEdit = false
  dialogTitle: string

  form: FormGroup

  channels$: Observable<Channel[]>
  rentals$: Observable<Rental[]>

  filteredRentals$: Observable<Rental[]>

  channelAdjustments$ = new BehaviorSubject<ChannelAdjustment[]>([])

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private amplitudeService: AmplitudeService,
    private channelAdjustmentService: ChannelAdjustmentService,
    private dialogRef: MatDialogRef<EditChannelAdjustmentDialogComponent, EditChannelAdjustmentDialogResult>,
    @Inject(MAT_DIALOG_DATA) private data: EditChannelAdjustmentDialogInput
  ) {
    super()

    const channelAdjustment = data.channelAdjustment || null

    this.isEdit = !!(channelAdjustment && channelAdjustment.id)
    this.dialogTitle = this.getDialogTitle()

    this.buildForm(channelAdjustment)
  }

  ngOnInit() {
    this.channels$ = this.store.select(selectAllApiChannelViews).pipe(
      map((channels) => {
        return lodash.sortBy(channels, (channel) => channel.friendlyName.toLowerCase())
      }),
      map((channels) => R.concat(channels)([tokeetDashboardChannel]))
    )

    this.rentals$ = this.store.select(selectAllRentals).pipe(
      map((rentals) => {
        return lodash.sortBy(rentals, (rental) => (rental.name + '').toLowerCase())
      })
    )

    this.filteredRentals$ = combineLatest(
      this.rentals$,
      this.channelAdjustments$,
      this.form.get('channelId').valueChanges.pipe(startWith(null as string))
    ).pipe(
      map(([rentals, channelAdjustments, channelId]) => {
        if (!channelId) return rentals

        const assignedRentals = {}

        channelAdjustments.forEach((ca) => {
          if (this.isEdit && ca.id === this.data.channelAdjustment.id) return
          if (ca.channelId !== channelId) return

          ca.rentalIds.forEach((rentalId) => {
            assignedRentals[rentalId] = true
          })
        })

        rentals = rentals.filter((rental) => !assignedRentals[rental.id])

        return rentals
      }),
      tap((rentals) => {
        const formControl = this.form.get('rentalIds')

        const rentalIds = lodash.keyBy(rentals, (rental) => rental.id)
        let selectedRentals = formControl.value as string[]

        selectedRentals = selectedRentals.filter((rentalId) => rentalIds[rentalId])

        formControl.setValue(selectedRentals)
      })
    )

    this.channelAdjustmentService
      .all()
      .pipe(
        tap((channelAdjustments: any) => {
          this.channelAdjustments$.next(channelAdjustments)
        })
      )
      .subscribe()
  }

  @SaveForm()
  onSubmit(form: FormGroup) {
    if (form.invalid) return

    const data = form.getRawValue() as ChannelAdjustment

    const channelAdjustment = new ChannelAdjustment({
      id: this.isEdit ? this.data.channelAdjustment.id : null,
      channelId: data.channelId,
      rentalIds: data.rentalIds,
    })

    if (data.adjustAmount) {
      channelAdjustment.adjustAmount = data.adjustAmount
      channelAdjustment.adjustPercent = 0
    } else if (data.adjustPercent) {
      channelAdjustment.adjustPercent = data.adjustPercent
      channelAdjustment.adjustAmount = 0
    } else {
      this.toaster.error('You must specify either Adjust Amount or Adjust Percent.')
      return
    }

    form.disable()

    this.saveChannelAdjustment(channelAdjustment)
      .pipe(
        tap((result) => {
          this.amplitudeService.logEvent(`add-channel-adjustment`)
          this.toaster.success('Channel adjustment saved successfully.')

          this.updateChannelAdjustments(result.channelAdjustments)
          this.close(result)
        }),
        catchError((error) => {
          this.toaster.error('Unable to save channel adjustment.')
          form.enable()
          this.toggleAdjustFields()

          return throwError(error)
        })
      )
      .subscribe()
  }

  close(result: EditChannelAdjustmentDialogResult = null) {
    this.dialogRef.close(result)
  }

  get adjustType(): AdjustType {
    return this.form && this.form.get('adjustType').value
  }

  private saveChannelAdjustment(channelAdjustment: ChannelAdjustment): Observable<EditChannelAdjustmentDialogResult> {
    const task = this.isEdit
      ? this.channelAdjustmentService.updateChannelAdjustment(channelAdjustment.id, channelAdjustment)
      : this.channelAdjustmentService.addChannelAdjustment(channelAdjustment)

    return task.pipe(
      map((result: any) => {
        let channelAdjustments: any = this.channelAdjustments$.value

        if (this.isEdit) {
          channelAdjustments = channelAdjustments.map((ca) => {
            return ca.id === result.id ? result : ca
          })
        } else {
          channelAdjustments = this.channelAdjustments$.value.concat(result)
        }

        return { channelAdjustment, channelAdjustments }
      })
    )
  }

  private updateChannelAdjustments(channelAdjustments: ChannelAdjustment[]) {
    this.store.dispatch(LoadChannelAdjustmentsComplete({ channelAdjustments }))
  }

  private buildForm(channelAdjustment: ChannelAdjustment) {
    const form = (this.form = this.formBuilder.group({
      channelId: [null, [Validators.required]],

      rentalIds: [[], [Validators.required]],

      adjustType: [null],
      adjustPercentChecked: [null],

      adjustAmount: [
        null,
        [
          (control: AbstractControl) => {
            return this.adjustType ? Validators.required(control) : null
          },
        ],
      ],

      adjustPercent: [
        null,
        [
          (control: AbstractControl) => {
            return this.adjustType ? Validators.required(control) : null
          },
        ],
      ],
    }))

    this.subscribeForm()

    if (this.isEdit) {
      form.get('channelId').setValue(channelAdjustment.channelId)
      form.get('channelId').disable()

      form.get('rentalIds').setValue(channelAdjustment.rentalIds)
    }

    if (channelAdjustment && channelAdjustment.adjustAmount) {
      form.get('adjustType').setValue('amount' as AdjustType)
      form.get('adjustAmount').setValue(channelAdjustment.adjustAmount)
    }

    if (channelAdjustment && channelAdjustment.adjustPercent) {
      form.get('adjustType').setValue('percent' as AdjustType)
      form.get('adjustPercent').setValue(channelAdjustment.adjustPercent)
    }

    if (!form.get('adjustType').value) {
      form.get('adjustType').setValue('amount' as AdjustType)
    }
  }

  private subscribeForm() {
    this.form
      .get('adjustType')
      .valueChanges.pipe(
        distinctUntilChanged(),
        tap((value: AdjustType) => {
          this.toggleAdjustFields(value)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private toggleAdjustFields(adjustType?: AdjustType) {
    if (!adjustType) {
      adjustType = this.form.get('adjustType').value
    }

    const adjustAmount = this.form.get('adjustAmount') as FormControl
    const adjustPercent = this.form.get('adjustPercent') as FormControl

    const control1 = adjustType === 'amount' ? adjustPercent : adjustAmount

    control1.reset(null, { onlySelf: true })
    control1.disable({ onlySelf: true })

    const control2 = adjustType === 'amount' ? adjustAmount : adjustPercent

    control2.enable({ onlySelf: true })
  }

  private getDialogTitle() {
    return `${this.isEdit ? 'Edit' : 'Add'} Channel Adjustment`
  }
}
